import {defineStore} from 'pinia';
import {useAdminStore} from '@core/store/AdminStore';
import {useEnvironmentStore} from '@internalDevportal/store/EnvironmentStore';
import {useSettingsStore} from '@core/store/SettingsStore';
import {useMenuStore} from '@core/store/MenuStore';
import {useTeamStore} from '@internalDevportal/store/TeamStore';
import {useSpecStore} from '@internalDevportal/store/SpecStore';
import {useWalkthroughStore} from '@core/store/WalkthroughStore';
import ModulesService from '@core/services/ModulesService';
import {ref} from 'vue';
import httpService from '@core/services/AxiosApiService';
import {getAppVersion} from '../utils/appVersion';

interface UserInfo {
  access_token?: string;
  profile?: {
    sub?: string;
    email?: string;
    name?: string;
  };
}

export const useUserStore = defineStore(
  'UserStore',
  () => {
    const admin = useAdminStore();
    const environment = useEnvironmentStore();
    const settingsStore = useSettingsStore();
    const menuStore = useMenuStore();
    const teamStore = useTeamStore();
    const specStore = useSpecStore();
    const walkthroughStore = useWalkthroughStore();

    const user = ref<UserInfo>({});
    const loggedIn = ref(false);
    const applications = ref<any[]>([]);
    const roles = ref<string | undefined>(undefined);
    let hydratedSub: string | null = null;

    function setSession(userInfo: UserInfo) {
      user.value = userInfo;
      loggedIn.value = true;
      localStorage.setItem('appVersionAtLastLogin', getAppVersion());
      httpService.setAccessToken(userInfo.access_token ?? '');
    }

    async function login(userInfo: UserInfo) {
      const sub = userInfo.profile?.sub ?? null;
      setSession(userInfo);

      if (sub && hydratedSub === sub) {
        return;
      }

      const coreLoads = [
        settingsStore.getStyles(),
        settingsStore.getImages(),
        settingsStore.getLanguages(),
        admin.getRoles(),
      ];
      const portalLoads = ModulesService.internalDeveloperPortalEnabled
        ? [
            environment.getEnvironments(),
            specStore.getSpecs(),
            specStore.getLatestSpecs(),
            specStore.getLastUpdatedSpecs(),
            teamStore.loadTeams(),
            menuStore.loadMenuItems(),
          ]
        : [];
      await Promise.allSettled([...coreLoads, ...portalLoads]);

      hydratedSub = sub;

      await walkthroughStore.loadProgress();
      if (!walkthroughStore.isSeen('create-application')) {
        walkthroughStore.startWalkthrough('create-application');
      }
    }

    function logout() {
      user.value = {};
      loggedIn.value = false;
      hydratedSub = null;
      httpService.setAccessToken('');
      admin.$reset();
      environment.$reset();
    }

    function $reset() {
      user.value = {};
      loggedIn.value = false;
      hydratedSub = null;
      applications.value = [];
      roles.value = undefined;
    }

    return {
      user,
      loggedIn,
      applications,
      roles,
      setSession,
      login,
      logout,
      $reset,
    };
  },
  {persist: true},
);
